import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Typography, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Button, useMediaQuery, useTheme, Box, Grid } from "@material-ui/core";
import { PendingButton, DataTable } from "@ploy-ui/core";
import { DployTextField, DployFormControl, DployAutocomplete } from "@ploy-ui/form-fields";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { useService } from "@ploy-lib/calculation";
import { BaseCamelCasedResource } from "@ploy-lib/rest-resources";
import { legacyApiResourceUrl } from "@ploy-lib/core";
import { useFetcher, useResource } from "@rest-hooks/core";
import { useStatefulResource } from "@rest-hooks/legacy";
import { LoginResource } from "@ploy-lib/rest-resources";
import { useAppLoad } from "../../appLoadContext";
export const Fulfillment = props => {
    return (_jsx(RefinancingAndOrFulfillment, { ...props, financingCode: "FULFILLMENT" }));
};
export const FulfillmentReadOnly = props => {
    return (_jsx(RefinancingAndOrFulfillment, { ...props, readOnly: true, financingCode: "FULFILLMENT" }));
};
export const Refinancing = props => {
    return (_jsx(RefinancingAndOrFulfillment, { ...props, financingCode: "REFINANCING" }));
};
export const RefinancingReadOnly = props => {
    return (_jsx(RefinancingAndOrFulfillment, { ...props, readOnly: true, financingCode: "REFINANCING" }));
};
class RefinancingContractResource extends BaseCamelCasedResource {
    constructor() {
        super(...arguments);
        this.contractId = 0;
        this.text = "";
        this.customerName = "";
        // Calculated
        this.termLength = "";
        this.dateOptions = [];
    }
    pk() {
        return `${this.contractId} - ${this.financingCode} - ${this.appInstanceId}`;
    }
    static list() {
        const endpoint = super.list();
        return endpoint.extend({
            schema: [this],
            fetch: async ({ financingCode, appInstanceId, ...params }) => {
                const data = await endpoint.fetch({ financingCode, ...params });
                return (data?.rows?.map(c => ({ ...c, financingCode, appInstanceId })) ?? []);
            }
        });
    }
    static calculate() {
        const endpoint = this.create();
        return endpoint.extend({
            url: (params) => endpoint.url({ ...params, action: "Calculate" }),
            fetch: async ({ financingCode, appInstanceId }, body) => {
                const result = await endpoint.fetch({ financingCode, action: "Calculate" }, { calculationRow: body });
                return { ...result?.calculationRow, financingCode, appInstanceId };
            }
        });
    }
    static save() {
        const endpoint = this.create();
        return endpoint.extend({
            schema: [this],
            url: ({ financingCode, appInstanceId }) => this.listUrl({ financingCode, appInstanceId, action: "Save" }),
            fetch: async ({ financingCode, appInstanceId }, body) => {
                await endpoint.fetch({ financingCode, action: "Save" }, { rows: body });
                return { ...body, financingCode, appInstanceId };
            }
        });
    }
}
RefinancingContractResource.urlRoot = legacyApiResourceUrl("Refinancing/{action}");
const commonButtonColors = [
    "inherit",
    "primary",
    "secondary",
    "default"
];
function isButtonColor(color) {
    return commonButtonColors.includes(color);
}
const commonButtonVariants = ["text", "outlined", "contained"];
function isButtonVariant(variant) {
    return commonButtonVariants.includes(variant);
}
const RefinancingAndOrFulfillment = (props) => {
    const { 
    //readOnly = false,
    field: { name }, options: { buttonText }, financingCode, form: { setFieldValue }, className, color, variant, fullWidth, disabled, margin } = props;
    const { id: appInstanceId } = useAppLoad();
    const buttonColor = isButtonColor(color) ? color : undefined;
    const buttonVariant = isButtonVariant(variant) ? variant : undefined;
    const firstDateService = useService("Main", "SetFirstDueDate");
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadedContracts = useResource(RefinancingContractResource.list(), appInstanceId
        ? {
            action: "Load",
            appInstanceId,
            financingCode
        }
        : null) ?? [];
    const saveContracts = useFetcher(RefinancingContractResource.save());
    const [selection, setSelection] = useState({});
    const [saveError, setSaveError] = useState();
    useEffect(() => {
        setSelection(Object.fromEntries(loadedContracts.map(c => [c.contractId, c]) ?? []));
    }, [loadedContracts]);
    const onClose = () => {
        setSelection(Object.fromEntries(loadedContracts.map(c => [c.contractId, c]) ?? []));
        setOpen(false);
    };
    const onOpen = () => {
        setOpen(true);
    };
    const onSave = async () => {
        if (!appInstanceId)
            return;
        try {
            setSaveError(undefined);
            await saveContracts({ financingCode, appInstanceId }, Object.values(selection), [
                [
                    RefinancingContractResource.list(),
                    {
                        action: "Load",
                        financingCode,
                        appInstanceId
                    },
                    ids => ids
                ]
            ]);
            await firstDateService?.(undefined, null);
            //form.setFieldValue(name, Number.isNaN(value) ? 1 : value + 1);
            setFieldValue(name, 0 //selectedRows.reduce((sum, item) => sum + item.refinancedAmount, 0)
            );
            setOpen(false);
        }
        catch (e) {
            let message = e.message;
            if (e.response.headers.get("content-type").includes("json")) {
                const data = await e.response.json();
                if (data.ErrorMessage)
                    message = data.ErrorMessage;
            }
            setSaveError(message);
        }
    };
    return (_jsxs(DployFormControl, { margin: margin, fullWidth: fullWidth, className: className, children: [_jsx(PendingButton, { fullWidth: fullWidth, color: buttonColor, variant: buttonVariant, disabled: disabled, size: "large", onClick: onOpen, children: buttonText }), _jsxs(Dialog, { fullScreen: fullScreen, maxWidth: "lg", open: open, onClose: onClose, "aria-labelledby": `${financingCode}-dialog-title`, children: [_jsx(DialogTitle, { id: `${financingCode}-dialog-title`, children: buttonText }), _jsxs(DialogContent, { children: [props.placeholder && (_jsx(DialogContentText, { color: "textPrimary", children: props.placeholder })), open && (_jsx(ContractsSelection, { financingCode: financingCode, appInstanceId: appInstanceId, onSelectionChange: data => setSelection(Object.fromEntries(data.map(d => [d.contractId, selection[d.contractId] ?? d]))), onDateSelect: (id, data) => setSelection({ ...selection, [id]: data }), selection: selection }))] }), _jsxs(DialogActions, { children: [saveError && (_jsx(Box, { flexGrow: 1, ml: 2, children: _jsx(Typography, { color: "error", children: saveError }) })), _jsx(Button, { onClick: onClose, color: "primary", variant: "contained", children: _jsx(FormattedMessage, { id: "core.refinancing.close", defaultMessage: "Close" }) }), _jsx(PendingButton, { onClick: onSave, color: "primary", variant: "contained", success: false, children: _jsx(FormattedMessage, { id: "core.refinancing.save", defaultMessage: "Save" }) })] })] })] }));
};
function ContractsSelection(props) {
    const { financingCode, appInstanceId, onSelectionChange, onDateSelect, selection } = props;
    const { vendor } = useResource(LoginResource.status(), {});
    const [searchValue, setSearchValue] = useState();
    const [topContracts, setTopContracts] = useState(Object.values(selection));
    const onSearch = (value) => {
        setTopContracts(Object.values(selection));
        setSearchValue(value);
    };
    const { data: searchedContracts, loading } = useStatefulResource(RefinancingContractResource.list(), searchValue != null && appInstanceId
        ? {
            action: "Search",
            searchString: searchValue,
            vendorId: vendor?.id,
            appInstanceId,
            financingCode
        }
        : null);
    const calculate = useFetcher(RefinancingContractResource.calculate());
    const contracts = topContracts.concat(searchedContracts?.filter(s => !topContracts?.some(t => t.contractId === s.contractId)) ?? []);
    const columns = [
        {
            field: "contractId",
            title: (_jsx(FormattedMessage, { id: "dploy.fulfillmentSection.contractId.header", defaultMessage: "Contract number" }))
        },
        {
            field: "text",
            title: (_jsx(FormattedMessage, { id: "dploy.fulfillmentSection.tekst.header", defaultMessage: "Text." }))
        },
        {
            field: "customerName",
            title: (_jsx(FormattedMessage, { id: "dploy.fulfillmentSection.customerName.header", defaultMessage: "Customer" }))
        },
        {
            field: "matureDate",
            title: (_jsx(FormattedMessage, { id: "dploy.fulfillmentSection.matureDate.header", defaultMessage: "Maturity Date." })),
            render: rowData => selection[rowData.contractId] != null ? (_jsx(Typography, { children: selection[rowData.contractId].matureDate })) : null
        },
        {
            field: "termLength",
            title: (_jsx(FormattedMessage, { id: "dploy.fulfillmentSection.termLength.header", defaultMessage: "Interval" })),
            render: rowData => selection[rowData.contractId] != null ? (_jsx(Typography, { children: rowData.termLength })) : null
        },
        {
            field: "dateOptions",
            title: (_jsx(FormattedMessage, { id: "dploy.fulfillmentSection.dateOptions.header", defaultMessage: "Calculate from" })),
            render: rowData => {
                const selected = selection[rowData.contractId] != null;
                return (_jsx(DployAutocomplete, { style: { width: 190 }, disabled: (rowData.dateOptions?.length ?? 0) <= 0, items: selected ? rowData.dateOptions : [], getItemLabel: item => item.text, value: selected
                        ? rowData.dateOptions?.find(item => item.key === selection[rowData.contractId].dateOption)
                        : null, searchable: false, onChange: (e, item) => onDateSelect(rowData.contractId, {
                        ...rowData,
                        dateOption: item?.key,
                        matureDate: item?.key,
                        refinancedAmount: item
                            ? Number(item.amount.replace(",", "."))
                            : undefined
                    }) }));
            }
        },
        {
            field: "refinancedAmount",
            title: (_jsx(FormattedMessage, { id: "dploy.fulfillmentSection.refinancedAmount.header", defaultMessage: "Outstanding debt." })),
            render: rowData => selection[rowData.contractId]?.refinancedAmount ? (_jsx(FormattedNumber, { value: selection[rowData.contractId].refinancedAmount ?? 0, format: "currency_full" })) : null
        }
    ];
    return (_jsxs(Grid, { container: true, direction: "column", spacing: 2, children: [_jsx(Grid, { item: true, children: _jsx(SearchField, { onSearch: onSearch, searching: loading }) }), _jsx(Grid, { item: true, children: _jsx(DataTable, { columns: columns, data: contracts.map(c => ({
                        ...c,
                        tableData: { checked: selection[c.contractId] != null }
                    })), listCellsBreakpoint: "sm", options: {
                        search: false,
                        grouping: false,
                        showEmptyDataSourceMessage: false,
                        emptyRowsWhenPaging: false,
                        paging: true,
                        header: true,
                        toolbar: false,
                        selection: true,
                        showSelectAllCheckbox: false
                    }, onSelectionChange: async (data, row) => {
                        onSelectionChange(data);
                        if (appInstanceId &&
                            row &&
                            data.includes(row) &&
                            (row.dateOptions?.length ?? 0) <= 0)
                            await calculate({ financingCode, appInstanceId }, row);
                    } }) })] }));
}
function SearchField(props) {
    const { onSearch, searching } = props;
    const [searchValue, setSearchValue] = useState("");
    return (_jsxs("div", { children: [_jsx(DployTextField, { variant: "outlined", value: searchValue, onChange: e => {
                    setSearchValue(e.target.value);
                    return e;
                }, onKeyDown: e => {
                    if (e.key === "Enter")
                        onSearch(searchValue);
                } }), _jsx(PendingButton, { pending: searching, size: "large", variant: "outlined", onClick: () => onSearch(searchValue), success: false, children: _jsx(FormattedMessage, { id: "core.refinancing.search", defaultMessage: "Search" }) })] }));
}
//# sourceMappingURL=Refinancing.js.map